import React, { useEffect, useState } from 'react';
import { sendDesktopLink } from '../api/leads';
import './MobileDesktopBanner.css';

// Accent color now comes from CSS design tokens (--accent-primary)

function readDismissalFlag(key) {
  if (typeof window === 'undefined') {
    return false;
  }

  return window.sessionStorage.getItem(key) === '1';
}

function readBackendErrorDetail(error) {
  const detail = error?.response?.data?.detail;

  if (typeof detail === 'string' && detail.trim()) {
    return detail.trim();
  }

  return null;
}

export default function MobileDesktopBanner({ page, headline, subtext }) {
  const dismissedKey = `mobile_banner_dismissed_${page}`;
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(() => (readDismissalFlag(dismissedKey) ? 'hidden' : 'idle'));
  const [helperText, setHelperText] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [viewportReady, setViewportReady] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const query = window.matchMedia('(max-width: 768px)');
    const sync = () => setIsMobile(query.matches);
    sync();
    setViewportReady(true);
    query.addEventListener?.('change', sync);
    query.addListener?.(sync);
    return () => {
      query.removeEventListener?.('change', sync);
      query.removeListener?.(sync);
    };
  }, []);

  useEffect(() => {
    setStatus(readDismissalFlag(dismissedKey) ? 'hidden' : 'idle');
  }, [dismissedKey]);

  useEffect(() => {
    if (status !== 'success') {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setStatus('hidden');
    }, 4000);

    return () => window.clearTimeout(timer);
  }, [status]);

  const handleClose = () => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(dismissedKey, '1');
    }
    setStatus('hidden');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (status === 'loading') {
      return;
    }

    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setHelperText('Enter an email address to send the desktop link.');
      setStatus('error');
      return;
    }

    setHelperText('');
    setStatus('loading');

    try {
      await sendDesktopLink(trimmedEmail, page);
      if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(dismissedKey, '1');
      }
      setEmail('');
      setHelperText('');
      setStatus('success');
    } catch (error) {
      const backendDetail = readBackendErrorDetail(error);
      const nextHelperText = backendDetail || 'Could not send the link. Check the email and try again.';
      setHelperText(nextHelperText);
      setStatus('error');
    }
  };

  if (!viewportReady || !isMobile || status === 'hidden') {
    return null;
  }

  const isBusy = status === 'loading';

  return (
    <aside
      className={`mobile-desktop-banner mobile-desktop-banner--${page}`}
      data-state={status}
    >
      <div className="mobile-desktop-banner__inner">
        <button
          type="button"
          className="mobile-desktop-banner__close"
          onClick={handleClose}
          aria-label="Dismiss desktop banner"
          disabled={isBusy}
        >
          <span aria-hidden="true">&times;</span>
        </button>

        {status === 'success' ? (
          <div className="mobile-desktop-banner__success" role="status" aria-live="polite">
            <p className="mobile-desktop-banner__success-label">Link sent</p>
            <p className="mobile-desktop-banner__success-text">
              Check your inbox. See you on desktop.
            </p>
          </div>
        ) : (
          <>
            <div className="mobile-desktop-banner__copy">
              <p className="mobile-desktop-banner__eyebrow">Mobile Heads-Up</p>
              <h2 className="mobile-desktop-banner__headline">{headline}</h2>
              <p className="mobile-desktop-banner__subtext">{subtext}</p>
            </div>

            <form className="mobile-desktop-banner__form" onSubmit={handleSubmit}>
              <div className="mobile-desktop-banner__controls">
                <input
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  className="mobile-desktop-banner__input"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  disabled={isBusy}
                  aria-label="Email address"
                  required
                />
                <button type="submit" className="mobile-desktop-banner__submit" disabled={isBusy}>
                  <span className="mobile-desktop-banner__submit-content">
                    {isBusy && <span className="mobile-desktop-banner__spinner" aria-hidden="true" />}
                    <span>{isBusy ? 'Sending' : 'Send Link'}</span>
                  </span>
                </button>
              </div>
              <p
                className={`mobile-desktop-banner__helper ${
                  status === 'error'
                    ? 'mobile-desktop-banner__helper--error'
                    : 'mobile-desktop-banner__helper--idle'
                }`}
                role={status === 'error' ? 'alert' : undefined}
                aria-live="polite"
              >
                {status === 'error' ? helperText : 'We only use this to send the desktop link.'}
              </p>
            </form>
          </>
        )}
      </div>
    </aside>
  );
}
